/**
 * TextToSpeech Component
 *
 * A speaker button component for reading text aloud using speech synthesis.
 * Provides play/pause/stop controls and visual feedback.
 *
 * Features:
 * - Play/pause toggle with visual state
 * - Stop button during playback
 * - Handles long text with chunking
 * - Customizable voice and speed
 * - Accessible with ARIA labels
 *
 * @example
 * <TextToSpeech text={articleContent} />
 *
 * @example
 * <TextToSpeech
 *   text={content}
 *   rate={1.2}
 *   showControls
 *   onStart={() => console.log('Started')}
 *   onEnd={() => console.log('Ended')}
 * />
 */

import React, { useCallback, useState } from 'react';
import {
  FaVolumeUp,
  FaVolumeMute,
  FaPause,
  FaPlay,
  FaStop,
  FaCog,
} from 'react-icons/fa';
import useSpeechSynthesis from '../../hooks/useSpeechSynthesis';

/**
 * TextToSpeech component for reading text aloud
 * @param {Object} props - Component props
 * @param {string} props.text - Text content to read aloud
 * @param {string} props.language - Language code (default: 'en-US')
 * @param {number} props.rate - Speech rate 0.5-2 (default: 1)
 * @param {number} props.pitch - Speech pitch 0-2 (default: 1)
 * @param {function} props.onStart - Called when speech starts
 * @param {function} props.onEnd - Called when speech ends
 * @param {boolean} props.showControls - Show expanded controls (pause/stop)
 * @param {boolean} props.showSettings - Show voice/rate settings panel
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.buttonClassName - CSS classes for button
 * @param {string} props.size - Button size: 'sm', 'md', 'lg' (default: 'md')
 * @param {boolean} props.disabled - Disable the button
 * @param {string} props.label - Optional label text next to button
 */
const TextToSpeech = ({
  text,
  language = 'en-US',
  rate: initialRate = 1,
  pitch: initialPitch = 1,
  onStart,
  onEnd,
  showControls = false,
  showSettings = false,
  className = '',
  buttonClassName = '',
  size = 'md',
  disabled = false,
  label,
}) => {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const {
    isSpeaking,
    isPaused,
    voices,
    currentVoice,
    rate,
    browserSupported,
    speak,
    speakLongText,
    cancel,
    pause,
    resume,
    setVoice,
    setRate,
    setPitch,
    getVoicesByLanguage,
  } = useSpeechSynthesis({
    language,
    defaultRate: initialRate,
    defaultPitch: initialPitch,
    onStart,
    onEnd,
  });

  const handlePlayClick = useCallback(() => {
    if (isSpeaking && !isPaused) {
      if (showControls) {
        pause();
      } else {
        cancel();
      }
    } else if (isPaused) {
      resume();
    } else if (text) {
      // Use speakLongText for content longer than 200 chars
      if (text.length > 200) {
        speakLongText(text);
      } else {
        speak(text);
      }
    }
  }, [isSpeaking, isPaused, text, speak, speakLongText, pause, resume, cancel, showControls]);

  const handleStopClick = useCallback(() => {
    cancel();
  }, [cancel]);

  // Size classes
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
  };

  const iconSizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  // If browser doesn't support speech synthesis, don't render
  if (!browserSupported) {
    return null;
  }

  // Get available voices for the selected language
  const availableVoices = getVoicesByLanguage(language);

  const getButtonTitle = () => {
    if (disabled || !text) return 'No text to read';
    if (isPaused) return 'Resume reading';
    if (isSpeaking) return showControls ? 'Pause reading' : 'Stop reading';
    return 'Read aloud';
  };

  return (
    <div className={`text-to-speech-container inline-flex items-center gap-2 ${className}`}>
      {/* Main play/pause button */}
      <button
        type="button"
        onClick={handlePlayClick}
        disabled={disabled || !text}
        title={getButtonTitle()}
        aria-label={getButtonTitle()}
        aria-pressed={isSpeaking}
        className={`
          relative rounded-full flex items-center justify-center
          transition-all duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-offset-2
          ${sizeClasses[size]}
          ${
            disabled || !text
              ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
              : isSpeaking
              ? 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500'
              : 'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500'
          }
          ${buttonClassName}
        `}
      >
        {/* Speaking animation */}
        {isSpeaking && !isPaused && (
          <span className="absolute inset-0 rounded-full bg-primary-400 animate-pulse opacity-50" />
        )}

        {/* Icon */}
        <span className="relative z-10">
          {disabled || !text ? (
            <FaVolumeMute className={iconSizeClasses[size]} />
          ) : isPaused ? (
            <FaPlay className={iconSizeClasses[size]} />
          ) : isSpeaking ? (
            showControls ? (
              <FaPause className={iconSizeClasses[size]} />
            ) : (
              <FaVolumeUp className={iconSizeClasses[size]} />
            )
          ) : (
            <FaVolumeUp className={iconSizeClasses[size]} />
          )}
        </span>
      </button>

      {/* Stop button (shown when playing and showControls is true) */}
      {showControls && isSpeaking && (
        <button
          type="button"
          onClick={handleStopClick}
          title="Stop reading"
          aria-label="Stop reading"
          className={`
            rounded-full flex items-center justify-center
            bg-red-500 text-white hover:bg-red-600
            transition-colors duration-200
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500
            ${sizeClasses[size]}
          `}
        >
          <FaStop className={iconSizeClasses[size]} />
        </button>
      )}

      {/* Settings button */}
      {showSettings && (
        <button
          type="button"
          onClick={() => setSettingsOpen(!settingsOpen)}
          title="Voice settings"
          aria-label="Voice settings"
          aria-expanded={settingsOpen}
          className={`
            rounded-full flex items-center justify-center
            bg-neutral-200 text-neutral-600 hover:bg-neutral-300
            transition-colors duration-200
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-400
            ${sizeClasses[size]}
          `}
        >
          <FaCog className={`${iconSizeClasses[size]} ${settingsOpen ? 'animate-spin' : ''}`} />
        </button>
      )}

      {/* Label */}
      {label && (
        <span className="text-sm text-neutral-600">{label}</span>
      )}

      {/* Speaking indicator */}
      {isSpeaking && !showControls && (
        <span className="text-xs text-primary-600 animate-pulse">
          {isPaused ? 'Paused' : 'Speaking...'}
        </span>
      )}

      {/* Settings panel */}
      {showSettings && settingsOpen && (
        <div className="absolute top-full mt-2 left-0 bg-white rounded-lg shadow-lg border border-neutral-200 p-4 z-50 min-w-[250px]">
          {/* Voice selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Voice
            </label>
            <select
              value={currentVoice?.name || ''}
              onChange={(e) => setVoice(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {(availableVoices.length > 0 ? availableVoices : voices).map((voice) => (
                <option key={voice.name} value={voice.name}>
                  {voice.name} ({voice.lang})
                </option>
              ))}
            </select>
          </div>

          {/* Rate slider */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Speed: {rate.toFixed(1)}x
            </label>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={rate}
              onChange={(e) => setRate(parseFloat(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-neutral-500">
              <span>Slower</span>
              <span>Faster</span>
            </div>
          </div>

          {/* Close button */}
          <button
            type="button"
            onClick={() => setSettingsOpen(false)}
            className="w-full px-3 py-2 bg-neutral-100 text-neutral-700 rounded-md text-sm hover:bg-neutral-200 transition-colors"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * TextToSpeechInline - A compact inline version
 * For use in list items, cards, etc.
 */
export const TextToSpeechInline = ({
  text,
  language = 'en-US',
  disabled = false,
  className = '',
}) => {
  const { isSpeaking, browserSupported, speak, cancel } = useSpeechSynthesis({
    language,
  });

  const handleClick = useCallback(() => {
    if (isSpeaking) {
      cancel();
    } else if (text) {
      speak(text);
    }
  }, [isSpeaking, text, speak, cancel]);

  if (!browserSupported) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || !text}
      aria-label={isSpeaking ? 'Stop reading' : 'Read aloud'}
      className={`
        p-2 rounded-full transition-colors duration-200
        ${
          isSpeaking
            ? 'text-primary-600 bg-primary-50 hover:bg-primary-100'
            : 'text-neutral-400 hover:text-primary-500 hover:bg-primary-50'
        }
        ${disabled || !text ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
    >
      {isSpeaking ? (
        <FaStop className="w-4 h-4" />
      ) : (
        <FaVolumeUp className="w-4 h-4" />
      )}
    </button>
  );
};

/**
 * ReadAloudButton - Simple button variant with text label
 */
export const ReadAloudButton = ({
  text,
  language = 'en-US',
  buttonText = 'Read Aloud',
  speakingText = 'Stop Reading',
  className = '',
}) => {
  const { isSpeaking, browserSupported, speak, speakLongText, cancel } =
    useSpeechSynthesis({ language });

  const handleClick = useCallback(() => {
    if (isSpeaking) {
      cancel();
    } else if (text) {
      if (text.length > 200) {
        speakLongText(text);
      } else {
        speak(text);
      }
    }
  }, [isSpeaking, text, speak, speakLongText, cancel]);

  if (!browserSupported) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!text}
      className={`
        inline-flex items-center gap-2 px-4 py-2 rounded-lg
        transition-colors duration-200
        ${
          isSpeaking
            ? 'bg-primary-600 text-white hover:bg-primary-700'
            : 'bg-primary-500 text-white hover:bg-primary-600'
        }
        ${!text ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}
      `}
    >
      {isSpeaking ? <FaStop className="w-4 h-4" /> : <FaVolumeUp className="w-4 h-4" />}
      <span>{isSpeaking ? speakingText : buttonText}</span>
    </button>
  );
};

export default TextToSpeech;
